import React, { useState } from 'react';
import { Button } from './ui/button';
import GlassCard from './GlassCard';
import { Badge } from './ui/badge';
import {
  Plus,
  MessageSquare,
  Check,
  Trash2,
  MoreVertical,
  Edit2,
  Star,
  Clock,
  PanelLeft
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { toast } from 'sonner';

const ChatHistorySidebar = ({ 
  conversations = [], 
  activeConversationId, 
  onSelectConversation, 
  onNewConversation,
  onDeleteConversation,
  onRenameConversation,
  onToggleStar,
  isCollapsed,
  onToggleCollapse 
}) => {
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState(null);
  
  const handleRename = (convId, currentTitle) => {
    setRenamingId(convId);
    setRenameValue(currentTitle || 'New Discovery Session');
  };
  
  const saveRename = async (convId) => {
    if (onRenameConversation) {
      await onRenameConversation(convId, renameValue);
    }
    setRenamingId(null);
  };
  
  const toggleStar = async (convId, e) => {
    if (e) e.stopPropagation();
    if (onToggleStar) {
      onToggleStar(convId);
    }
  };

  const openDeleteDialog = (convId) => {
    setConversationToDelete(convId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (conversationToDelete) {
      onDeleteConversation(conversationToDelete);
    }
    setDeleteDialogOpen(false);
    setConversationToDelete(null);
  };

  const renderConversationItem = (conv) => {
    const isActive = activeConversationId === conv.id;
    const isCompleted = conv.conversation_status === 'completed';
    
    return (
      <div
        key={conv.id}
        className={`group relative rounded-lg px-3 py-2.5 cursor-pointer transition-all hover:bg-gray-100 dark:hover:bg-dark-bg-hover ${
          isActive ? 'bg-primary/10 hover:bg-primary/15' : ''
        }`}
        onClick={() => onSelectConversation(conv.id)}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-text-gray dark:text-dark-text-secondary flex-shrink-0" />
              {renamingId === conv.id ? (
                <input
                  type="text"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onBlur={() => saveRename(conv.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveRename(conv.id);
                    if (e.key === 'Escape') setRenamingId(null);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                  className="flex-1 text-sm font-medium bg-transparent border-b border-primary focus:outline-none text-text-dark dark:text-dark-text px-1"
                />
              ) : (
                <>
                  <p className="font-medium text-text-dark dark:text-dark-text text-sm truncate">
                    {conv.title || 'New Discovery Session'}
                  </p>
                  {conv.starred && (
                    <Star className="h-3 w-3 fill-yellow-400 text-yellow-400 flex-shrink-0" />
                  )}
                  {isCompleted && (
                    <Badge className="text-xs bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 border-0 px-1.5 py-0">
                      <Check className="h-2.5 w-2.5" />
                    </Badge>
                  )}
                </>
              )}
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                onClick={(e) => e.stopPropagation()}
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleRename(conv.id, conv.title);
                }}
              >
                <Edit2 className="h-4 w-4 mr-2" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => toggleStar(conv.id, e)}
              >
                <Star className={`h-4 w-4 mr-2 ${conv.starred ? 'fill-yellow-400 text-yellow-400' : ''}`} />
                {conv.starred ? 'Unstar' : 'Star'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  openDeleteDialog(conv.id);
                }}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'completed':
        return { icon: <Check className="h-3 w-3" />, className: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300', label: 'Ready' };
      case 'in-progress':
        return { icon: <Clock className="h-3 w-3" />, className: 'bg-orange-100 text-orange-700 dark:bg-dark-bg-tertiary dark:text-orange-400', label: 'Active' };
      case 'abandoned':
        return { icon: null, className: 'bg-gray-100 text-gray-600 dark:bg-dark-bg-tertiary dark:text-gray-400', label: 'Paused' };
      default:
        return { icon: <Clock className="h-3 w-3" />, className: 'bg-orange-100 text-orange-700 dark:bg-dark-bg-tertiary dark:text-orange-400', label: 'Active' };
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  if (isCollapsed) {
    return (
      <div className="h-full w-full glass-strong border-r border-gray-200 dark:border-[#2e2e2d] flex flex-col items-center py-4">
        <Button
          onClick={onToggleCollapse}
          variant="ghost"
          size="sm"
          className="w-10 h-10 p-0 mb-4"
        >
          <PanelLeft className="h-4 w-4" />
        </Button>
        <Button
          onClick={onNewConversation}
          variant="ghost"
          size="sm"
          className="w-10 h-10 p-0 mb-4 hover:bg-primary/10"
        >
          <Plus className="h-5 w-5 text-primary" />
        </Button>
        <div className="flex-1 overflow-y-auto w-full flex flex-col items-center">
          {conversations.slice(0, 5).map((conv) => (
            <button
              key={conv.id}
              onClick={() => onSelectConversation(conv.id)}
              className={`w-full p-2 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-dark-bg-hover transition-colors ${
                activeConversationId === conv.id ? 'bg-primary/10' : ''
              }`}
            >
              <MessageSquare className="h-5 w-5 text-text-gray dark:text-dark-text-secondary" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full glass-strong border-r border-gray-200 dark:border-[#2e2e2d] flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-[#2e2e2d]">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-text-dark dark:text-dark-text">
            Discovery Sessions
          </h3>
          <Button
            onClick={onToggleCollapse}
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
          >
            <PanelLeft className="h-4 w-4" />
          </Button>
        </div>
        <Button
          onClick={onNewConversation}
          className="w-full gradient-primary text-white rounded-xl font-semibold"
        >
          <Plus className="mr-2 h-4 w-4" />
          Start New Discovery
        </Button>
      </div>

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto p-2">
        {conversations.length === 0 ? (
          <div className="p-4 text-center text-text-gray dark:text-dark-text-secondary text-sm">
            No discovery sessions yet. Start your first one!
          </div>
        ) : (
          <div className="space-y-1">
            {/* Starred Section */}
            {conversations.filter(c => c.starred).length > 0 && (
              <>
                <div className="px-2 py-1.5 flex items-center gap-1.5">
                  <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                  <span className="text-xs font-semibold text-text-gray dark:text-dark-text-secondary uppercase tracking-wide">
                    Starred
                  </span>
                </div>
                {conversations
                  .filter(c => c.starred)
                  .map((conv) => renderConversationItem(conv))}
              </>
            )}
            
            {/* Recent Section */}
            {conversations.filter(c => c.starred).length > 0 && conversations.filter(c => !c.starred).length > 0 && (
              <div className="px-2 py-1.5 mt-3">
                <span className="text-xs font-semibold text-text-gray dark:text-dark-text-secondary uppercase tracking-wide">
                  Recent
                </span>
              </div>
            )}
            
            {conversations
              .filter(c => !c.starred)
              .map((conv) => renderConversationItem(conv))}
          </div>
        )}
      </div>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Conversation?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this discovery session and all its messages. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="px-6 py-2.5 rounded-lg font-medium">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-2.5 rounded-lg border-0 shadow-md transition-all"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ChatHistorySidebar;
